import { useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../store';
import { SubscriptionPlansPanel } from '../components/SubscriptionPlansPanel';
import { fetchCurrentUser } from '../services/authService';
import { subscriptionService } from '../services/subscriptionService';
import { setUser } from '../store/slices/authSlice';
import type { User } from '../types';

function subscriptionReturnTo(location: ReturnType<typeof useLocation>): string | null {
  const params = new URLSearchParams(location.search);
  const fromQuery = params.get('returnTo');
  if (fromQuery && fromQuery.startsWith('/')) return fromQuery;

  const state = location.state;
  if (typeof state !== 'object' || state === null || !('returnTo' in state)) return null;
  const v = (state as { returnTo?: unknown }).returnTo;
  return typeof v === 'string' && v.startsWith('/') ? v : null;
}

export function SubscriptionPage() {
  const user = useSelector((s: RootState) => s.auth.user);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const returnTo = subscriptionReturnTo(location);

  const refreshUser = useCallback(async () => {
    const updated = await fetchCurrentUser();
    if (updated) {
      dispatch(
        setUser({
          ...updated,
          role: updated.role as User['role'],
          plan: updated.plan,
        })
      );
    }
    return updated;
  }, [dispatch]);

  /** Confirm PayU payment server-side (webhook fallback); retry while gateway settles. */
  const syncPendingWithRetries = useCallback(async (): Promise<boolean> => {
    const delaysMs = [0, 2000, 4000, 8000];
    for (const delay of delaysMs) {
      if (delay > 0) {
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
      try {
        const result = await subscriptionService.syncPendingCheckout();
        if (result.activated) return true;
      } catch {
        // Endpoint may be missing until backend deploy — still refresh user
      }
    }
    return false;
  }, []);

  useEffect(() => {
    void (async () => {
      await syncPendingWithRetries();
      await refreshUser();
    })();
  }, [syncPendingWithRetries, refreshUser]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void (async () => {
          await syncPendingWithRetries();
          await refreshUser();
        })();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [syncPendingWithRetries, refreshUser]);

  const handleCheckoutSuccess = () => {
    if (returnTo) navigate(returnTo, { replace: true });
  };

  return (
    <div className="max-w-lg mx-auto w-full">
      <h1 className="text-2xl font-black text-slate-900 mb-1">Subscription</h1>
      <p className="text-slate-500 text-sm mb-4">Choose a plan to participate in live bidding.</p>

      {returnTo ? (
        <Link
          to={returnTo}
          className="inline-flex items-center gap-1.5 text-sm font-bold text-blue-600 mb-6 hover:underline"
        >
          ← Back to auction
        </Link>
      ) : null}

      <div className="bg-white rounded-2xl border border-slate-100 p-4 sm:p-6 shadow-sm">
        <div className="mb-5 pb-4 border-b border-slate-100">
          <h2 className="text-lg font-black text-slate-900">Membership plans</h2>
          <p className="text-slate-500 text-sm mt-1">Elevate your procurement experience.</p>
        </div>
        <SubscriptionPlansPanel user={user} returnToPath={returnTo} />
        {user && user.plan !== 'FREE' && returnTo ? (
          <button
            type="button"
            onClick={handleCheckoutSuccess}
            className="mt-4 w-full text-sm font-bold text-blue-600 border border-blue-200 rounded-xl py-2 hover:bg-blue-50"
          >
            Continue to auction →
          </button>
        ) : null}
      </div>
    </div>
  );
}
